<template>
  <v-bottom-navigation class="d-md-none bottom-nav">
    <nav>
      <v-list
        class="justify-center d-flex flex-row bg-transparent toolbar-links text-primary py-0 h-100"
        variant="plain"
        tag="ul"
        :aria-label="t(l.label_main_navigation)"
        role="list"
      >
        <li v-for="tab in tabs" :key="tab.path || tab.href" v-show="!tab.disabled">
          <v-list-item :href="tab.href" :to="tab.path" active-class="active-nav-link" class="h-100">
            <template #default="{ isActive }">
              <v-icon class="mb-1" :icon="`${isActive ? 'fa:fas' : 'fa:fat'} ${tab.icon}`"></v-icon>
              <v-list-item-title :class="capitalizationClass" class="tab-text"> {{ t(tab.label) }}</v-list-item-title>
            </template>
          </v-list-item>
        </li>
      </v-list>
    </nav>
  </v-bottom-navigation>
</template>

<script setup lang="ts">
import config from '@/config/ConfigProvider';
import { useLocale, useLocalisation } from '@/plugins/translation';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { configHelper } from '@ilteducation/config';

const showGuides = ref<string>(config.value('showGuides'));
const showMaterials = ref<string>(config.value('showMaterials'));
const showGames = ref<string>(config.value('showGames'));

const { t, l } = useLocale();
const capitalizationClass = computed(() => {
  const { language } = useLocalisation();
  return language.value === 'en-GB' ? 'text-capitalize' : 'text-capitalize-first';
});
const { getAppUrl } = configHelper;
const countryCode = ref<string>(config.value('countryCode'));
const articlesUrl = getAppUrl('artiklar.begreppa');
const tabs: Array<{ href?: string; path?: string; label: string; icon: string; disabled: boolean }> = [
  { path: '/videos', icon: 'fa-camera-movie', label: l.tabs.films, disabled: false },
  { path: '/guides', icon: 'fa-book', label: l.tabs.guides_short, disabled: !showGuides.value },
  { path: '/materials', icon: 'fa-file-lines', label: l.tabs.materials, disabled: !showMaterials.value },
  { path: '/games', icon: 'fa-gamepad-modern', label: l.tabs.games, disabled: !showGames.value },
  {
    href: articlesUrl,
    icon: 'fa-newspaper',
    label: l.tabs.articles,
    disabled: countryCode.value !== 'se',
  },
];
const activeTab = ref<number>(0);
const route = useRoute();
watch(
  route,
  (newRoute) => {
    const tab = tabs.findIndex((t) => t.path && newRoute.path.startsWith(t.path));
    if (tab !== -1) {
      activeTab.value = tab;
    }
  },
  { immediate: true },
);
</script>
<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use '@/foundations/theme/styles' as s;
.bottom-nav {
  padding: 0 16px 0;
  z-index: 10;
}
.v-bottom-navigation {
  height: s.$bottom-navigation-and-safe-area !important;
}
:deep(.toolbar-links) {
  .v-list-item__spacer {
    display: none;
  }
  .v-list-item {
    opacity: 1;
    min-width: 48px;
    padding: 0 16px;
    .v-icon,
    .v-list-item__title {
      opacity: 1;
    }
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      color: var(--v-theme-primary) !important;
    }
    .v-list-item__content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
  .active-nav-link {
    $active-link-border-width: 2px;
    border-top-width: $active-link-border-width;
    color: var(--v-theme-primary);
    border-top-color: var(--v-theme-primary);
    .v-list-item__prepend,
    .v-list-item__content {
      margin-bottom: calc($active-link-border-width * -1); // trick to not make the text bounce
    }
  }
}

.tab-text {
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
}

.text-capitalize-first {
  text-transform: lowercase !important;
}

.text-capitalize-first::first-letter {
  text-transform: uppercase !important;
}
</style>
